/**
 * useFlows - React hooks for flows engine v1
 * Manages flow definitions, run history, and flow actions
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { Flow, FlowRun, FlowRunEvent } from '@/lib/api';
import {
  getFlows,
  getFlowRuns,
  getFlowRunEvents,
  enableFlow,
  disableFlow,
  setFlowPosture,
  triggerFlowRun as apiTriggerFlowRun,
} from '@/lib/api';
import type { FlowPosture } from '@/lib/api';
import { useWorkspace } from '@/providers/workspace-provider';

function normalizeFlowFilePath(path: string | null | undefined) {
  if (!path || typeof path !== 'string') return '';
  return path.replace(/^\/+/u, '').trim();
}

interface UseFlowsReturn {
  flows: Flow[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/** Fetch all flows with runtime state */
export function useFlows(): UseFlowsReturn {
  const { activeWorkspaceIds } = useWorkspace();
  const scopeKey = activeWorkspaceIds.join(',');
  const [flows, setFlows] = useState<Flow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchFlows = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const ids = scopeKey ? scopeKey.split(',') : [];
      const data = await getFlows(ids);
      setFlows(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch flows'));
    } finally {
      setLoading(false);
    }
  }, [scopeKey]);

  useEffect(() => {
    fetchFlows();
  }, [fetchFlows]);

  return {
    flows,
    loading,
    error,
    refetch: fetchFlows,
  };
}

interface UseFlowRunsOptions {
  /** Poll interval while a run is in flight or pollWhile is true. Default 5000. Set 0 to disable. */
  pollMs?: number;
  /** Extra poll trigger (e.g. Run now in flight before the new row appears). */
  pollWhile?: boolean;
}

interface UseFlowRunsReturn {
  runs: FlowRun[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

function isInFlightRunStatus(status: string | null | undefined): boolean {
  const s = String(status || '').toLowerCase();
  return s === 'running' || s === 'pending' || s === 'queued';
}

/** Fetch run history for a specific flow */
export function useFlowRuns(flowId: string, options: UseFlowRunsOptions = {}): UseFlowRunsReturn {
  const pollMs = options.pollMs ?? 5000;
  const pollWhile = options.pollWhile ?? false;
  const [runs, setRuns] = useState<FlowRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRuns = useCallback(async (opts?: { silent?: boolean }) => {
    if (!flowId) {
      setRuns([]);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      if (!opts?.silent) setLoading(true);
      setError(null);
      const data = await getFlowRuns(flowId);
      setRuns(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch flow runs'));
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [flowId]);

  useEffect(() => {
    void fetchRuns();
  }, [fetchRuns]);

  const hasInFlight = runs.some((r) => isInFlightRunStatus(r.status));

  useEffect(() => {
    if (!flowId || pollMs <= 0) return;
    if (!pollWhile && !hasInFlight) return;
    const id = window.setInterval(() => {
      void fetchRuns({ silent: true });
    }, pollMs);
    return () => window.clearInterval(id);
  }, [flowId, pollMs, pollWhile, hasInFlight, fetchRuns]);

  return {
    runs,
    loading,
    error,
    refetch: fetchRuns,
  };
}

interface UseFlowActionsReturn {
  enable: (id: string) => Promise<void>;
  disable: (id: string) => Promise<void>;
  setPosture: (id: string, mode: FlowPosture) => Promise<void>;
  triggerRun: (id: string, payload?: Record<string, unknown>) => Promise<string>;
  loading: boolean;
  error: Error | null;
}

/** Flow actions: enable, disable, trigger manual run */
export function useFlowActions(): UseFlowActionsReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const enable = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      await enableFlow(id);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to enable flow'));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const disable = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      await disableFlow(id);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to disable flow'));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const setPosture = useCallback(async (id: string, mode: FlowPosture) => {
    try {
      setLoading(true);
      setError(null);
      await setFlowPosture(id, mode);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to set flow posture'));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const triggerRun = useCallback(async (id: string, payload: Record<string, unknown> = {}): Promise<string> => {
    try {
      setLoading(true);
      setError(null);
      const { runId } = await apiTriggerFlowRun(id, payload);
      return runId;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to trigger flow'));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    enable,
    disable,
    setPosture,
    triggerRun,
    loading,
    error,
  };
}

/** Resolved flow definition row matching an open flow document tab (sidebar list parity). */
export function useFlowByDocumentId(documentId: string, filePath?: string | null) {
  const { flows, loading, error, refetch } = useFlows();

  const flow = useMemo(() => {
    if (!flows.length) return null;
    const byId = flows.find((f) => f.id === documentId);
    if (byId) return byId;
    const fp = normalizeFlowFilePath(filePath);
    if (fp.length > 0) {
      const byPath = flows.find((f) => normalizeFlowFilePath(f.file_path) === fp);
      if (byPath) return byPath;
    }
    return null;
  }, [flows, documentId, filePath]);

  return { flow, loading, error, refetch };
}

interface UseFlowRunEventsReturn {
  events: FlowRunEvent[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

interface UseFlowRunEventsOptions {
  pollMs?: number;
  pollWhile?: boolean;
}

/** Fetch lifecycle events for a single run */
export function useFlowRunEvents(
  flowId: string,
  runId: string | null,
  options: UseFlowRunEventsOptions = {},
): UseFlowRunEventsReturn {
  const pollMs = options.pollMs ?? 5000;
  const pollWhile = options.pollWhile ?? false;
  const [events, setEvents] = useState<FlowRunEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchEvents = useCallback(async (opts?: { silent?: boolean }) => {
    if (!flowId || !runId) {
      setEvents([]);
      setLoading(false);
      setError(null);
      return;
    }
    try {
      if (!opts?.silent) setLoading(true);
      setError(null);
      const data = await getFlowRunEvents(flowId, runId);
      setEvents(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch run events'));
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [flowId, runId]);

  useEffect(() => {
    void fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (!flowId || !runId || pollMs <= 0 || !pollWhile) return;
    const id = window.setInterval(() => {
      void fetchEvents({ silent: true });
    }, pollMs);
    return () => window.clearInterval(id);
  }, [flowId, runId, pollMs, pollWhile, fetchEvents]);

  return { events, loading, error, refetch: fetchEvents };
}
